import React, { useState, useRef, useEffect, useCallback } from 'react';

const DOMAIN_PROMPTS = {
  ops: 'Du bist der DevBox OPS Assistent. Du kennst alle Server, Container, Backups, Services und das gesamte Oekosystem.',
  sec: 'Du bist der DevBox Security Assistent. Du kennst VPN, Firewall, Authentik, Fail2ban, SSL-Zertifikate und Zugangsmanagement.',
  crm: 'Du bist der DevBox CRM Assistent. Du kennst Kunden, Projekte, Rechnungen, Zeiterfassung und Business-Prozesse.',
  comm: 'Du bist der DevBox Kommunikations-Assistent. Du kennst Matrix, Ntfy, Miniflux, E-Mail und alle Kommunikationskanaele.',
  dev: 'Du bist der DevBox DEV Assistent. Du kennst Forgejo, Ollama, CI/CD und alle Entwicklungs-Tools.',
  private: 'Du bist der Private Assistent. Du kennst Intel Platform, Job Tracker, Journal und persoenliche Daten.',
  data: 'Du bist der Data Assistent. Du kennst alle Datenbanken, Flows, ML-Modelle und Storage.',
  ai: 'Du bist der AI Assistent. Du kennst alle KI-Modelle, Agents, Memory und Workflows.',
  finance: 'Du bist der Finance Assistent. Du kennst Portfolio, Trading-Signale, Paper Trading und Marktdaten.',
  wiki: 'Du bist der Wiki Assistent. Du kennst BookStack, Outline, SilverBullet, MkDocs und alle Dokumentation.',
  projects: 'Du bist der Projekt Assistent. Du kennst Tasks, Repos, Roadmaps und Sprint-Planung.',
  master: 'Du bist der Master Assistent. Du hast den Ueberblick ueber das gesamte DevBox Ecosystem.',
  orchestrator: 'Du bist der Orchestrator Assistent. Du kennst Service-Graphen, Dependencies und System-Flows.',
  home: 'Du bist der Home Assistent. Du kennst Heimnetzwerk, Geraete und Smart Home.',
  creative: 'Du bist der Creative Assistent. Du kennst Design, Brainstorming und kreative Projekte.',
  media: 'Du bist der Media Assistent. Du kennst Medien-Verwaltung, Nextcloud und Dateien.',
  admin: 'Du bist der Admin Assistent. Du kennst Benutzerverwaltung, Billing und System-Administration.',
  apps: 'Du bist der Apps Assistent. Du kennst Container, Service-Registry und App-Health.',
};

const GLOBAL_PROMPT = 'Du bist der DevBox Global Agent. Du kennst das gesamte Oekosystem mit allen 18 Domains, ~55 Containern, 3 Servern, Backup-System, VPN und allen Projekten.';

const DOMAIN_SUGGESTIONS = {
  ops: ['Was laeuft gerade auf dem Server?', 'Zeig den Backup-Status', 'Welche Container brauchen am meisten RAM?'],
  sec: ['Gibt es aktive Bedrohungen?', 'Welche SSL-Certs laufen bald ab?', 'Wie ist der VPN-Status?'],
  crm: ['Welche Rechnungen sind offen?', 'Zeig aktive Projekte', 'Stunden diese Woche?'],
  comm: ['Letzte Alerts anzeigen', 'Matrix-Status pruefen', 'Ungelesene Feeds?'],
  finance: ['Wie steht mein Portfolio?', 'Aktive Trading-Signale?', 'Paper Trading P&L?'],
  wiki: ['Was wurde heute dokumentiert?', 'BookStack Shelves anzeigen', 'Letzte Notizen?'],
};

function getStoredAgent(domain) {
  try { return localStorage.getItem(`ki_agent_${domain}`) || 'domain'; } catch { return 'domain'; }
}

export default function KiPanel({ domain = 'ops', visible = false, onClose }) {
  const [agent, setAgent] = useState(() => getStoredAgent(domain));
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(() => crypto.randomUUID?.() || Date.now().toString());
  const [sessions, setSessions] = useState([]);
  const [showSessions, setShowSessions] = useState(false);
  const [domainContext, setDomainContext] = useState('');
  const [attachedFiles, setAttachedFiles] = useState([]);
  const chatEnd = useRef(null);

  // Listen for file-attach events from DomainExplorer
  useEffect(() => {
    const handler = (e) => {
      const { name, path, content } = e.detail || {};
      if (name && content) {
        setAttachedFiles(prev => {
          if (prev.some(f => f.path === path)) return prev;
          return [...prev, { name, path, content }];
        });
      }
    };
    window.addEventListener('explorer:file-select', handler);
    return () => window.removeEventListener('explorer:file-select', handler);
  }, []);

  // Check localStorage for cross-stack file attachment
  useEffect(() => {
    if (!visible) return;
    try {
      const stored = localStorage.getItem('attachedFile');
      if (stored) {
        const { name, path, content } = JSON.parse(stored);
        if (name && content) {
          setAttachedFiles(prev => {
            if (prev.some(f => f.path === path)) return prev;
            return [...prev, { name, path, content }];
          });
        }
        localStorage.removeItem('attachedFile');
      }
    } catch {}
  }, [visible]);

  useEffect(() => { chatEnd.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

  // Load domain context on mount
  useEffect(() => {
    if (!visible) return;
    fetch(`/api/ki/context/domain/${domain}`).then(r => r.json()).then(d => {
      setDomainContext(d.system_prompt || '');
    }).catch(() => {});
  }, [domain, visible]);

  // Load recent sessions
  useEffect(() => {
    if (!visible) return;
    fetch(`/api/ki/chat/sessions?app=${domain}&limit=5`).then(r => r.json()).then(d => {
      setSessions(d.data || []);
    }).catch(() => {});
  }, [domain, visible]);

  const switchAgent = (a) => {
    setAgent(a);
    setDropdownOpen(false);
    try { localStorage.setItem(`ki_agent_${domain}`, a); } catch {}
  };

  const getPrompt = () => {
    const base = agent === 'global' ? GLOBAL_PROMPT : (DOMAIN_PROMPTS[domain] || `Du bist der ${domain.toUpperCase()} Assistent.`);
    let prompt = domainContext || base;
    if (attachedFiles.length > 0) {
      prompt += attachedFiles.map(f => `\n\n--- Datei: ${f.name} ---\n${f.content}`).join('');
    }
    return prompt;
  };

  const saveMessages = useCallback((msgs) => {
    if (msgs.length < 1) return;
    const toSave = msgs.slice(-2); // Save last 2 (user + assistant)
    fetch('/api/ki/chat/save', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ session_id: sessionId, app: domain, messages: toSave.map(m => ({ role: m.role, content: m.content, model: 'claude-haiku' })) }),
    }).catch(() => {});
  }, [sessionId, domain]);

  const send = async (text) => {
    const msg = text || input.trim();
    if (!msg || loading) return;
    setInput('');
    const newMsgs = [...messages, { role: 'user', content: msg }];
    setMessages(newMsgs);
    setLoading(true);
    try {
      fetch('/api/billing/consume', { method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'ai.chat', domain, amount: 1 }) }).catch(() => {});
      const r = await fetch('/api/agency/chat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: msg, history: messages.slice(-10), system_prompt: getPrompt() }),
      });
      const d = await r.json();
      const reply = d.answer || d.response || d.error || 'Keine Antwort';
      const updatedMsgs = [...newMsgs, { role: 'assistant', content: reply }];
      setMessages(updatedMsgs);
      saveMessages(updatedMsgs);
    } catch (e) {
      setMessages(prev => [...prev, { role: 'assistant', content: `Fehler: ${e.message}` }]);
    }
    setLoading(false);
  };

  const newSession = () => {
    setSessionId(crypto.randomUUID?.() || Date.now().toString());
    setMessages([]);
    setShowSessions(false);
  };

  const loadSession = async (sid) => {
    try {
      const r = await fetch(`/api/ki/chat/history?app=${domain}&limit=50`);
      const d = await r.json();
      const sessionMsgs = (d.data || []).filter(m => m.session_id === sid).reverse();
      setSessionId(sid);
      setMessages(sessionMsgs.map(m => ({ role: m.role, content: m.content })));
      setShowSessions(false);
    } catch { /* ignore */ }
  };

  const clearHistory = () => {
    if (sessionId) {
      fetch(`/api/ki/chat/session/${sessionId}`, { method: 'DELETE' }).catch(() => {});
    }
    setMessages([]);
    newSession();
  };

  if (!visible) return null;

  const accent = `var(--accent-${domain}, var(--accent, #73daca))`;
  const suggestions = DOMAIN_SUGGESTIONS[domain] || [];
  const agentLabel = agent === 'global' ? 'Global Agent' : `${domain.toUpperCase()}-Agent`;

  return (
    <div className="eco-ki-panel">
      {/* Header */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '6px var(--gap-sm)', borderBottom: '1px solid var(--border)', position: 'relative',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer' }}
          onClick={() => setDropdownOpen(!dropdownOpen)}>
          <span style={{ fontSize: 12 }}>{'\uD83E\uDD16'}</span>
          <span style={{ fontSize: 'var(--font-size-xs)', fontWeight: 600, color: accent }}>{agentLabel}</span>
          <span style={{ fontSize: 9, color: 'var(--text-muted)' }}>{'\u25BE'}</span>
        </div>
        <div style={{ display: 'flex', gap: 4 }}>
          <button onClick={() => setShowSessions(!showSessions)} title="Sessions" style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', fontSize: 12 }}>{'\uD83D\uDCCB'}</button>
          <button onClick={newSession} title="Neue Session" style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', fontSize: 12 }}>+</button>
          <button onClick={clearHistory} title="History loeschen" style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', fontSize: 12 }}>{'\uD83D\uDDD1'}</button>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', fontSize: 16 }}>{'\u00D7'}</button>
        </div>

        {/* Agent Dropdown */}
        {dropdownOpen && (
          <div style={{
            position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 10,
            background: 'var(--bg-secondary)', border: '1px solid var(--border)',
            borderRadius: 'var(--border-radius)', boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
          }}>
            <div onClick={() => switchAgent('domain')} style={{
              padding: '8px 12px', cursor: 'pointer', fontSize: 'var(--font-size-xs)',
              display: 'flex', alignItems: 'center', gap: 6,
              background: agent === 'domain' ? 'var(--bg-tertiary)' : 'transparent',
            }}>
              {agent === 'domain' && <span style={{ color: accent }}>{'\u2713'}</span>}
              <span>{domain.toUpperCase()}-Agent</span>
              <span style={{ fontSize: 9, color: 'var(--text-muted)', marginLeft: 'auto' }}>nur diese Page</span>
            </div>
            <div onClick={() => switchAgent('global')} style={{
              padding: '8px 12px', cursor: 'pointer', fontSize: 'var(--font-size-xs)',
              display: 'flex', alignItems: 'center', gap: 6,
              background: agent === 'global' ? 'var(--bg-tertiary)' : 'transparent',
            }}>
              {agent === 'global' && <span style={{ color: accent }}>{'\u2713'}</span>}
              <span>Globaler Agent</span>
              <span style={{ fontSize: 9, color: 'var(--text-muted)', marginLeft: 'auto' }}>alle Domains</span>
            </div>
          </div>
        )}

        {/* Sessions Dropdown */}
        {showSessions && (
          <div style={{
            position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 10,
            background: 'var(--bg-secondary)', border: '1px solid var(--border)',
            borderRadius: 'var(--border-radius)', boxShadow: '0 4px 12px rgba(0,0,0,0.3)', maxHeight: 200, overflow: 'auto',
          }}>
            <div style={{ padding: '6px 12px', fontSize: 'var(--font-size-xs)', color: 'var(--text-muted)', borderBottom: '1px solid var(--border)' }}>Letzte Sessions</div>
            {sessions.map(s => (
              <div key={s.session_id} onClick={() => loadSession(s.session_id)} style={{
                padding: '6px 12px', cursor: 'pointer', fontSize: 'var(--font-size-xs)',
                borderBottom: '1px solid var(--border)',
                background: s.session_id === sessionId ? 'var(--bg-tertiary)' : 'transparent',
              }}>
                <div style={{ fontWeight: 500 }}>{s.msg_count} Nachrichten</div>
                <div style={{ fontSize: 10, color: 'var(--text-muted)' }}>{s.started ? new Date(s.started).toLocaleString('de-DE') : ''}</div>
              </div>
            ))}
            {sessions.length === 0 && <div style={{ padding: '12px', fontSize: 'var(--font-size-xs)', color: 'var(--text-muted)' }}>Keine gespeicherten Sessions</div>}
          </div>
        )}
      </div>

      {/* Domain context indicator */}
      {domainContext && (
        <div style={{ padding: '4px var(--gap-sm)', fontSize: 10, color: 'var(--text-muted)', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: 4 }}>
          <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#9ece6a', display: 'inline-block' }} />
          Kontext geladen
        </div>
      )}

      {/* Chat */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <div style={{ flex: 1, overflow: 'auto', padding: 'var(--gap-sm)', display: 'flex', flexDirection: 'column', gap: 'var(--gap-xs)' }}>
          {messages.length === 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--gap-sm)', padding: 'var(--gap-md) 0' }}>
              <div style={{ fontSize: 'var(--font-size-sm)', color: 'var(--text-muted)', textAlign: 'center' }}>{agentLabel}</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                {suggestions.map((s, i) => (
                  <button key={i} onClick={() => send(s)} className="btn" style={{ fontSize: 'var(--font-size-xs)', textAlign: 'left', justifyContent: 'flex-start' }}>{s}</button>
                ))}
              </div>
            </div>
          )}
          {messages.map((m, i) => (
            <div key={i} style={{
              alignSelf: m.role === 'user' ? 'flex-end' : 'flex-start', maxWidth: '90%',
              padding: '6px 10px', borderRadius: 'var(--border-radius)', fontSize: 'var(--font-size-xs)',
              background: m.role === 'user' ? 'var(--bg-tertiary)' : 'var(--bg-primary)',
              whiteSpace: 'pre-wrap', wordBreak: 'break-word',
            }}>{m.content}</div>
          ))}
          {loading && <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--text-muted)' }}>Claude denkt...</div>}
          <div ref={chatEnd} />
        </div>
        {attachedFiles.length > 0 && (
          <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', padding: '4px var(--gap-sm)', borderTop: '1px solid var(--border)' }}>
            {attachedFiles.map((f, i) => (
              <span key={f.path} style={{
                display: 'inline-flex', alignItems: 'center', gap: 3,
                padding: '1px 6px', borderRadius: 10, fontSize: 10,
                background: '#24283b', border: '1px solid #9ece6a', color: '#c0caf5',
              }}>
                {f.name}
                <span onClick={() => setAttachedFiles(prev => prev.filter((_, j) => j !== i))}
                  style={{ cursor: 'pointer', opacity: 0.6, fontSize: 12, lineHeight: 1 }}>&times;</span>
              </span>
            ))}
          </div>
        )}
        <div style={{ display: 'flex', gap: 4, padding: 'var(--gap-xs) var(--gap-sm)', borderTop: '1px solid var(--border)' }}>
          <input className="input" style={{ flex: 1, fontSize: 'var(--font-size-xs)' }} placeholder="Frage..." value={input}
            onChange={e => setInput(e.target.value)} onKeyDown={e => e.key === 'Enter' && send()} disabled={loading} />
          <button className="btn btn-primary" style={{ fontSize: 'var(--font-size-xs)', padding: '4px 8px' }} onClick={() => send()} disabled={loading}>{'\u2192'}</button>
        </div>
      </div>
    </div>
  );
}
